import { useState, useRef, useCallback } from "react";
import { useDevicesStore } from "@/features/devices";
import { useMeetingLayoutStore, type LayoutMode } from "@/features/meeting/store/meeting-layout.store";
import { vclog } from "@/features/meeting/hooks/vc-log";

export function useScreenShare(setLayoutMode: (mode: "auto" | "tiled" | "spotlight" | "sidebar" | "pinned") => void) {
  const [screenOn, setScreenOn] = useState(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const screenVideoRef = useRef<HTMLVideoElement | null>(null);
  // The layout in use when the share started; null while not sharing.
  const layoutBeforeShareRef = useRef<LayoutMode | null>(null);
  // The pins when the share started, to tell whether anyone pinned meanwhile.
  const pinsBeforeShareRef = useRef<readonly string[] | null>(null);

  const stopScreenShare = useCallback(() => {
    setScreenOn(false);
    setScreenStream((prev) => {
      prev?.getTracks().forEach((t) => t.stop());
      return null;
    });
    // Back to the layout the user was in before sharing. Returning to the
    // Settings preference instead turned Auto, Spotlight or Sidebar into Tiled
    // the moment a share ended.
    //
    // Unless the pins changed during the share. A "Pin for all" (or a pin of
    // your own) made while you were presenting already set the layout that
    // shows it; going back to the old layout dropped that pin on the
    // presenter's screen alone — everyone else stayed pinned. A host stopping
    // your share comes through here too.
    const before = layoutBeforeShareRef.current;
    const pinsBefore = pinsBeforeShareRef.current;
    layoutBeforeShareRef.current = null;
    pinsBeforeShareRef.current = null;
    const pinsNow = useMeetingLayoutStore.getState().pinnedIds;
    const pinnedMeanwhile = !!pinsBefore && pinsBefore.join("\n") !== pinsNow.join("\n");
    if (before && !pinnedMeanwhile) setLayoutMode(before === "dual-stream" ? "auto" : before);
  }, [setLayoutMode]);

  /** Resolves true once a share is running, false when none started. */
  const startScreenShare = useCallback(async (): Promise<boolean> => {
    try {
      // The chosen frame rate, as a ceiling rather than a target: a static
      // slide should not be re-encoded 30 times a second just because the
      // setting allows it. `ideal` so a source that cannot hit the number still
      // shares instead of failing the constraint.
      const fps = useDevicesStore.getState().screenShareFrameRate;
      const stream = await navigator.mediaDevices.getDisplayMedia({
        // Cap the capture at 1080p. Only frame rate was constrained here, so a
        // Retina display was captured at its full backing resolution and handed
        // to the encoder whole.
        //
        // Measured 2026-09-08 from a live session, reading the track's own
        // getSettings(): 3420x1858 with screenPixelRatio 2 — 6.35 megapixels a
        // frame. Against SCREEN_SHARE_MAX_BITRATE of 2.5 Mbps at 30 fps that is
        //
        //     3420 x 1858 x 30  =  190 Mpixel/s
        //     2 500 000 / 190e6 =  0.013 bits per pixel
        //
        // For comparison the H.323 presentation leg was starving at 0.020 bpp
        // and looked visibly bad. 1920x1080 is 2.07 MP against 6.35 MP, so this
        // is ~3x fewer pixels for the same bitrate — about 0.04 bpp — and cuts
        // encode cost proportionally.
        //
        // `max`, not `ideal`: a Retina source will happily hand over 3420 wide
        // if merely asked nicely, which is exactly what was happening. Nothing
        // is lost at 1080p on any realistic viewing setup, least of all on the
        // 1280x720 the H.323 leg scales to anyway.
        video: {
          frameRate: { ideal: fps, max: fps },
          width: { max: 1920 },
          height: { max: 1080 },
        },
        // Request tab/system audio too. The browser may not grant it (depends on
        // OS/source); if absent we simply share video only.
        audio: true,
      });

      const track = stream.getVideoTracks()[0];
      if (track) {
        // Fires when the user clicks the browser's native "Stop sharing" chip.
        track.onended = () => {
          stopScreenShare();
        };
        // What was actually captured, not what was asked for — `width/height:
        // {max}` lets the source hand back anything up to that bound, and a
        // Retina/scaled display or an odd-aspect-ratio window routinely does.
        // This is the only place that's visible; the encoding settings this
        // feeds (bitrate cap, codec) are logged separately where the producer
        // is created.
        const settings = track.getSettings()
        vclog('producer', 'screen capture settings', {
          requested: { fps, widthMax: 1920, heightMax: 1080 },
          captured: {
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate,
            aspectRatio: settings.aspectRatio,
            displaySurface: settings.displaySurface,
            deviceScaleFactor: (settings as { deviceScaleFactor?: number }).deviceScaleFactor,
          },
        })
      }

      setScreenStream(stream);
      setScreenOn(true);
      layoutBeforeShareRef.current = useMeetingLayoutStore.getState().layoutMode;
      pinsBeforeShareRef.current = useMeetingLayoutStore.getState().pinnedIds;
      setLayoutMode("sidebar");
      return true;
    } catch {
      // User cancelled the picker or the browser denied it. Nothing started,
      // so the layout is left exactly as it was.
      return false;
    }
  }, [setLayoutMode]);

  /**
   * Resolves true only when this call started a share — what a caller that
   * opened something alongside the picker (the meeting's PiP) needs to know to
   * take it back down when the picker is cancelled.
   */
  const toggleScreenShare = useCallback((): Promise<boolean> => {
    if (screenOn) {
      stopScreenShare();
      return Promise.resolve(false);
    }
    return startScreenShare().catch(() => false);
  }, [screenOn, startScreenShare, stopScreenShare]);

  // No attach here. The stage owns its video element and binds whatever is
  // being presented — this share or someone else's — as the element mounts
  // (MeetingScreen `attachPresentationVideo`). Binding the local share here as
  // well ran once per stream, before the stage's element existed in some
  // layouts, and could put this share over a remote one the viewer had picked.

  return {
    screenOn,
    screenStream,
    screenVideoRef,
    startScreenShare,
    stopScreenShare,
    toggleScreenShare,
  };
}
